/* eslint-disable no-param-reassign */
import Vue from 'vue';
import Vuex from 'vuex';
import createPersistedState from 'vuex-persistedstate';
import auth from '@/store/modules/auth';
import locationsModule from '@/store/modules/locations';
import event from '@/store/modules/event';
import user from '@/store/modules/user';
import userChats from '@/store/modules/userChats';
import subscription from '@/store/modules/subscription';
import Account from './store/modules/Account';
import locations from './store/modules/locations';
import shoppingCart from './store/modules/shoppingCart';
import shoppingList from './store/modules/shoppingList';
import category from './store/modules/category';
import banner from './store/modules/banner';

Vue.use(Vuex);

const store = new Vuex.Store({
  /**
   * it specifies exclusively the module 'auth' to be persistent across refresh and cache clean
   * the module 'auth' contains all the user login/tags information only
   */
  plugins: [
    createPersistedState({
      paths: ['auth', 'shoppingCart', 'shoppingList'],
    }),
  ],
  modules: {
    auth,
    locationsModule,
    Account,
    locations,
    event,
    shoppingCart,
    shoppingList,
    category,
    user,
    subscription,
    banner,
    userChats,
  },
  state: {
    selectedProduct: {},
    currentSelectedShoppingList: {},
    partnersFromPartnerGroup: [],
    subscriptions: [],
    userToUnsubscribe: {},
    partnerGroupUrl: '',
    userWantsToQuitOverlay: false,
    networkError: false,
    networkErrorRequest: '', // Which request results in networkError
    region: '',
    allLanguages: [],
    order: null,
    badgeInfo: 0,
    activatePopupByRouting: false,
    currentDetailsForEventPage: {},
    growthVitamins: [],
    hideHeaderAndFooter: false,
    userDetails: {},
    orderDetails: {},
    orderType: {},
    listOfTickets: {},
    recipientUserObject: {},
    frontEndType: '',
    notes: '',
    reviewPartnerEventId: 0,
    scrollDirectlyToItems: true,
  },
  mutations: {
    setScrollDirectlyToItems(state, scrollDirectlyToItems) {
      state.scrollDirectlyToItems = scrollDirectlyToItems;
    },
    setReviewPartnerEventId(state, reviewPartnerEventId) {
      state.reviewPartnerEventId = reviewPartnerEventId;
    },
    setHideHeaderAndFooter(state, hideHeaderAndFooter) {
      state.hideHeaderAndFooter = hideHeaderAndFooter;
    },
    setSelectedProduct(state, selectedProduct) {
      state.selectedProduct = selectedProduct;
    },
    decrementTimer(state) {
      state.growthVitamin.DiscountInfo.DiscountValidTime -= 1;
    },
    setCurrentSelectedShoppingList(state, currentSelectedShoppingList) {
      state.currentSelectedShoppingList = {};
      state.currentSelectedShoppingList = currentSelectedShoppingList;
    },
    setGrowthVitamins(state, gvdata) {
      state.growthVitamins = gvdata;
    },
    addGrowthVitamin(state, gvdata) {
      state.growthVitamins.push(gvdata);
    },
    setCurrentDetailsForEventPage(state, currentDetailsForEventPage) {
      state.currentDetailsForEventPage = currentDetailsForEventPage;
    },
    setUserToUnsubscribe(state, userToUnsubscribe) {
      state.userToUnsubscribe = userToUnsubscribe;
    },
    setPartnerGroupUrl(state, partnerGroupUrl) {
      state.partnerGroupUrl = partnerGroupUrl;
    },
    setNetworkError(state, networkError) {
      state.networkError = networkError;
    },
    setNetworkErrorRequest(state, networkErrorRequest) {
      state.networkErrorRequest = networkErrorRequest;
    },
    setUserWantsToQuitOverlay(state, userWantsToQuitOverlay) {
      state.userWantsToQuitOverlay = userWantsToQuitOverlay;
    },
    setRegion(state, region) {
      state.region = region;
    },
    setAllLanguages(state, allLanguages) {
      state.allLanguages = allLanguages;
    },
    setOrder(state, order) {
      state.order = order;
    },
    setActivatePopupByRouting(state, activatePopupByRouting) {
      state.activatePopupByRouting = activatePopupByRouting;
    },
    setPartnersFromPartnerGroup(state, partnersFromPartnerGroup) {
      state.partnersFromPartnerGroup = partnersFromPartnerGroup;
    },
    setBadgeInfo(state, badgeInfo) {
      state.badgeInfo = badgeInfo;
    },
    setUserDetails(state, userDetails) {
      state.userDetails = userDetails;
    },
    setOrderDetails(state, orderDetails) {
      state.orderDetails = orderDetails;
    },
    setOrderType(state, orderType) {
      state.orderType = orderType;
    },
    setListOfTickets(state, listOfTickets) {
      state.listOfTickets = listOfTickets;
    },
    setRecipientUserObject(state, recipientUserObject) {
      state.recipientUserObject = recipientUserObject;
    },
    setFrontEndType(state, frontEndType) {
      state.frontEndType = frontEndType;
    },
    setNotes(state, notes) {
      state.notes = notes;
    },
  },
  getters: {
    getUserDetails(state) {
      return state.userDetails;
    },
    getOrderDetails(state) {
      return state.orderDetails;
    },
    getOrderType(state) {
      return state.orderType;
    },
    getListOfTickets(state) {
      return state.listOfTickets;
    },
    getRecipientUserObject(state) {
      return state.recipientUserObject;
    },
    overviewData(state) {
      return state.overviewData;
    },
    getScrollDirectlyToItems(state) {
      return state.scrollDirectlyToItems;
    },
    getReviewPartnerEventId(state) {
      return state.reviewPartnerEventId;
    },
    getHideHeaderAndFooter(state) {
      return state.hideHeaderAndFooter;
    },
    getSelectedProduct(state) {
      return state.selectedProduct;
    },
    getGVTimer(state) {
      return state.growthVitamins[0].ExpiryUnixTime;
    },
    getCurrentSelectedShoppingList(state) {
      return state.currentSelectedShoppingList;
    },
    getGrowthVitamins(state) {
      return state.growthVitamins;
    },
    getUserToUnsubscribe(state) {
      return state.userToUnsubscribe;
    },
    getPartnerGroupUrl(state) {
      return state.partnerGroupUrl;
    },
    getNetworkError(state) {
      return state.networkError;
    },
    getNetworkErrorRequest(state) {
      return state.networkErrorRequest;
    },
    getUserWantsToQuitOverlay(state) {
      return state.userWantsToQuitOverlay;
    },
    getRegion(state) {
      return state.region;
    },
    getAllLanguages(state) {
      return state.allLanguages;
    },
    getOrder(state) {
      return state.order;
    },
    getActivatePopupByRouting(state) {
      return state.activatePopupByRouting;
    },
    getCurrentDetailsForEventPage(state) {
      return state.currentDetailsForEventPage;
    },
    getPartnersFromPartnerGroup(state) {
      return state.partnersFromPartnerGroup;
    },
    getBadgeInfo(state) {
      return state.badgeInfo;
    },
    getFrontEndType(state) {
      return state.frontEndType;
    },
    getNotes(state) {
      return state.notes;
    },
    isProfUser(state) {
      if (state.frontEndType === 'Prof') {
        return true;
      }
      return false;
    },
  },
});

export { store as default };
